import { useState } from "react";
import { Loader2, Plus, Star, Upload, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const DEFAULT_CATEGORIES = ["Mugs", "Glasses", "Salt & Pepper Sets", "Planters", "Decorative Pots", "Decor Items"];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const empty = {
  name: "", category: "Mugs", price: "", original_price: "", stock: "", image: "", extra_images: [],
  color: "", description: "", spec_dimensions: "", spec_clay: "", spec_care: "", featured: false,
};

const ProductForm = ({ initial, onSave, onCancel }) => {
  const { categories } = useShop();
  const CATEGORIES = categories.length ? categories.map((x) => x.name) : DEFAULT_CATEGORIES;
  const [f, setF] = useState(() => {
    if (!initial) return empty;
    return {
      name: initial.name, category: initial.category, price: initial.price, original_price: initial.original_price || "",
      stock: initial.stock, image: initial.image, extra_images: (initial.images || []).filter((u) => u !== initial.image),
      color: initial.color || "", description: initial.description || "",
      spec_dimensions: initial.specs?.Dimensions || initial.specs?.Capacity || "", spec_clay: initial.specs?.Clay || "",
      spec_care: initial.specs?.Care || "", featured: initial.featured || false,
    };
  });
  const [uploading, setUploading] = useState(false);
  const [urlDraft, setUrlDraft] = useState("");

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const addImage = (url) => {
    setF((s) => (s.image ? { ...s, extra_images: [...s.extra_images, url] } : { ...s, image: url }));
  };

  const uploadPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        addImage(`${BACKEND_URL}${r.data.url}`);
      }
      toast.success(files.length > 1 ? `${files.length} photos uploaded` : "Photo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed — try again");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const addUrl = () => {
    if (!urlDraft.trim()) return;
    addImage(urlDraft.trim());
    setUrlDraft("");
  };

  const removeExtra = (i) => setF((s) => ({ ...s, extra_images: s.extra_images.filter((_, x) => x !== i) }));

  const removeCover = () =>
    setF((s) => ({ ...s, image: s.extra_images[0] || "", extra_images: s.extra_images.slice(1) }));

  const makeCover = (i) =>
    setF((s) => {
      const next = s.extra_images[i];
      const extras = s.extra_images.filter((_, x) => x !== i);
      if (s.image) extras.unshift(s.image);
      return { ...s, image: next, extra_images: extras };
    });

  const submit = () => {
    const specs = {};
    if (f.spec_dimensions) specs[f.category === "Mugs" || f.category === "Glasses" ? "Capacity" : "Dimensions"] = f.spec_dimensions;
    if (f.spec_clay) specs.Clay = f.spec_clay;
    if (f.spec_care) specs.Care = f.spec_care;
    onSave({
      name: f.name, category: f.category, price: Number(f.price), original_price: f.original_price ? Number(f.original_price) : null,
      stock: Number(f.stock) || 0, image: f.image, images: [f.image, ...f.extra_images].filter(Boolean),
      color: f.color, description: f.description, specs, featured: f.featured,
    });
  };

  const inputCls = "h-11 w-full rounded-lg border border-linen bg-white px-3 text-sm outline-none focus:border-terra";

  return (
    <div className="space-y-4" data-testid="admin-product-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="mb-1.5 block text-xs font-semibold text-clay">Name</label>
          <input data-testid="product-form-name" value={f.name} onChange={set("name")} className={inputCls} placeholder="Wabi Sabi Organic Clay Mug" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-clay">Category</label>
          <Select value={f.category} onValueChange={(v) => setF((s) => ({ ...s, category: v }))}>
            <SelectTrigger data-testid="product-form-category" className="h-11 border-linen bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="mt-1 text-[10px] text-clay/50">Manage categories in the Categories tab</p>
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-clay">Finish / colour</label>
          <input value={f.color} onChange={set("color")} className={inputCls} placeholder="Raw Cream & Speckled Clay" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-clay">Price ₹</label>
          <input data-testid="product-form-price" type="number" value={f.price} onChange={set("price")} className={inputCls} placeholder="1450" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-clay">Compare-at price ₹ (optional)</label>
          <input type="number" value={f.original_price} onChange={set("original_price")} className={inputCls} placeholder="1800" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-clay">Stock quantity</label>
          <input data-testid="product-form-stock" type="number" value={f.stock} onChange={set("stock")} className={inputCls} placeholder="12" />
        </div>
        <div className="flex items-end justify-between rounded-lg border border-linen bg-white px-3.5 pb-2.5 pt-2">
          <label className="text-xs font-semibold text-clay">Feature on homepage</label>
          <Switch checked={f.featured} onCheckedChange={(v) => setF((s) => ({ ...s, featured: v }))} data-testid="product-form-featured" />
        </div>

        <div className="sm:col-span-2">
          <label className="mb-1.5 block text-xs font-semibold text-clay">
            Product photos <span className="font-normal text-clay/50">(first photo is the cover — tap the star on any photo to make it the cover, or the × to remove it)</span>
          </label>
          <div className="flex flex-wrap items-start gap-3">
            {f.image && (
              <div className="relative">
                <img src={f.image} alt="Cover" className="h-28 w-28 rounded-xl border-2 border-terra object-cover" data-testid="product-image-preview" />
                <span className="absolute -top-2 left-2 rounded-full bg-terra px-2 py-0.5 font-mono text-[8px] font-semibold uppercase tracking-widest text-white">Cover</span>
                <button
                  onClick={removeCover}
                  aria-label="Remove cover photo"
                  title="Remove cover photo (next photo becomes the cover)"
                  data-testid="product-cover-remove"
                  className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-espresso text-cream hover:bg-destructive"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            {f.extra_images.map((u, i) => (
              <div key={u + i} className="relative">
                <img src={u} alt={`Gallery ${i + 2}`} className="h-28 w-28 rounded-xl border border-linen object-cover" />
                <button
                  onClick={() => makeCover(i)}
                  aria-label={`Make photo ${i + 2} the cover`}
                  title="Make this the cover photo"
                  data-testid={`product-make-cover-${i}`}
                  className="absolute -left-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-white text-ochre shadow-sm ring-1 ring-linen transition-colors hover:bg-terra hover:text-white"
                >
                  <Star className="h-3 w-3" />
                </button>
                <button onClick={() => removeExtra(i)} aria-label="Remove photo" className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-espresso text-cream hover:bg-destructive">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            <label
              data-testid="product-photo-upload-button"
              className={`flex h-28 w-28 cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-linen bg-white text-clay/60 transition-colors hover:border-terra hover:text-terra ${uploading ? "pointer-events-none opacity-60" : ""}`}
            >
              {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
              <span className="px-2 text-center font-mono text-[9px] uppercase tracking-widest">{uploading ? "Uploading…" : "Upload"}</span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={uploadPhoto} data-testid="product-photo-upload-input" />
            </label>
          </div>
          <div className="mt-3 flex gap-2">
            <input value={urlDraft} onChange={(e) => setUrlDraft(e.target.value)} className={inputCls} placeholder="…or paste an image URL" data-testid="product-form-image-url" />
            <button type="button" onClick={addUrl} className="flex h-11 shrink-0 items-center gap-1.5 rounded-lg bg-clay px-4 text-xs font-semibold uppercase tracking-widest text-cream transition-colors hover:bg-terra">
              <Plus className="h-3.5 w-3.5" /> Add
            </button>
          </div>
        </div>

        <div className="sm:col-span-2">
          <label className="mb-1.5 block text-xs font-semibold text-clay">Description</label>
          <textarea value={f.description} onChange={set("description")} rows={3} className="w-full rounded-lg border border-linen bg-white px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-terra" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-clay">Capacity / dimensions</label>
          <input value={f.spec_dimensions} onChange={set("spec_dimensions")} className={inputCls} placeholder="350ml or 18cm × 22cm" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-clay">Clay</label>
          <input value={f.spec_clay} onChange={set("spec_clay")} className={inputCls} placeholder="Speckled stoneware" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-clay">Care</label>
          <input value={f.spec_care} onChange={set("spec_care")} className={inputCls} placeholder="Dishwasher & microwave safe" />
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onCancel} className="rounded-full border border-linen px-6 py-2.5 text-sm font-semibold text-clay hover:border-clay">Cancel</button>
        <button
          data-testid="product-form-save"
          onClick={submit}
          disabled={!f.name || !f.price || uploading}
          className="rounded-full bg-terra px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-terra-dark disabled:opacity-40"
        >
          {initial ? "Save changes" : "Add to shelf"}
        </button>
      </div>
    </div>
  );
};

export default ProductForm;
